import React, { useMemo, useState } from 'react';
import { MdOutlineWorkOutline } from 'react-icons/md';
import { SectionCard, Pill, SubHeading, Segmented, Note } from './ui';
import ChartFrame from '../charts/ChartFrame';
import HBarList from '../charts/HBarList';
import Meter from '../charts/Meter';
import { fmtNum, fmtCompact, fmtPct, employmentCharacter, labelize, safeArray, isNum, SECTOR_LABELS, EARNINGS_LABELS, WORKER_AGE_LABELS, DASH } from '../format';

const EARNINGS_KEYS = ['1250_or_less_usd_per_month', '1251_to_3333_usd_per_month', 'over_3333_usd_per_month'];
const EARNINGS_COLORS = ['var(--le-ramp-1)', 'var(--le-ramp-2)', 'var(--le-ramp-4)'];
const AGE_KEYS = ['29_or_younger', '30_to_54', '55_or_older'];

function SplitBar({ label, buckets, keys, colors, labels }) {
  const total = keys.reduce((s, k) => s + (isNum(buckets?.[k]) ? buckets[k] : 0), 0);
  if (!total) return null;
  return (
    <div className="le-split">
      <div className="le-split-label">{label} <span className="muted">· {fmtCompact(total)}</span></div>
      <div className="le-split-bar" role="img" aria-label={`${label} split`}>
        {keys.map((k, i) => {
          const v = isNum(buckets?.[k]) ? buckets[k] : 0;
          const pct = (v / total) * 100;
          return <div key={k} className="le-split-seg" style={{ width: `${pct}%`, background: colors[i] }} title={`${labels[k]}: ${fmtNum(v)} (${pct.toFixed(0)}%)`} />;
        })}
      </div>
      <div className="le-split-legend">
        {keys.map((k, i) => {
          const v = isNum(buckets?.[k]) ? buckets[k] : 0;
          return <span key={k}><span className="le-legend-swatch" style={{ '--swatch': colors[i] }} />{labels[k]} <strong>{((v / total) * 100).toFixed(0)}%</strong></span>;
        })}
      </div>
    </div>
  );
}

export default function EmploymentCard({ data, status }) {
  const radii = useMemo(() => safeArray(data?.radii).filter((r) => isNum(r.radius_miles)).sort((a, b) => a.radius_miles - b.radius_miles), [data]);
  const [radius, setRadius] = useState(5);
  const selected = radii.find((r) => r.radius_miles === radius) || radii[radii.length - 1] || null;
  const source = data?.source || {};
  const excluded = source.coverage?.states_excluded || {};
  const notes = safeArray(data?.notes);

  const sectors = useMemo(() => {
    if (!selected) return null;
    const jobs = selected.jobs_by_sector || {};
    const residents = selected.resident_worker_jobs_by_sector || {};
    const keys = Array.from(new Set([...Object.keys(jobs), ...Object.keys(residents)]));
    const rows = keys.map((k) => ({ key: k, label: labelize(k, SECTOR_LABELS), jobs: isNum(jobs[k]) ? jobs[k] : 0, residents: isNum(residents[k]) ? residents[k] : 0 }))
      .sort((a, b) => b.jobs - a.jobs);
    return { top: rows.slice(0, 8), all: rows };
  }, [selected]);

  const subtitle = data
    ? `${source.name || 'Census LODES'} · ${source.year || DASH} · cumulative radii · statistical job counts, not headcounts`
    : null;

  return (
    <SectionCard id="le-section-employment" icon={<MdOutlineWorkOutline size={20} />} title="Employment access" subtitle={subtitle} status={status} dataset={data}>
      {data ? (
        <>
          {Object.keys(excluded).length ? (
            <Note tone="warning">Coverage gaps: {Object.entries(excluded).map(([fips, reason]) => `state ${fips} — ${reason}`).join('; ')}</Note>
          ) : null}

          <div className="le-radii">
            {radii.map((r) => {
              const character = employmentCharacter(r.jobs_to_resident_worker_jobs_ratio);
              return (
                <button type="button" key={r.radius_miles} className={`le-radius-tile ${selected && selected.radius_miles === r.radius_miles ? 'active' : ''}`} onClick={() => setRadius(r.radius_miles)}>
                  <div className="le-radius-tile-head">
                    <span className="le-radius-tile-title">{r.radius_miles} mi</span>
                    <Pill tone={character.tone}>{character.label}</Pill>
                  </div>
                  <div className="le-radius-tile-nums">
                    <div><span className="le-radius-tile-value">{fmtCompact(r.jobs_located)}</span><span className="le-radius-tile-label">jobs located here</span></div>
                    <div><span className="le-radius-tile-value">{fmtCompact(r.resident_worker_jobs)}</span><span className="le-radius-tile-label">resident workers</span></div>
                  </div>
                  <Meter value={r.jobs_to_resident_worker_jobs_ratio} max={2} tone={isNum(r.jobs_to_resident_worker_jobs_ratio) && r.jobs_to_resident_worker_jobs_ratio >= 1 ? 'good' : 'accent'} label="Jobs to resident workers ratio" valueLabel={isNum(r.jobs_to_resident_worker_jobs_ratio) ? `${r.jobs_to_resident_worker_jobs_ratio.toFixed(2)}× jobs ÷ residents` : DASH} />
                  <div className="le-radius-tile-foot">{fmtNum(r.jobs_located_per_square_mile)} jobs / sq mi</div>
                </button>
              );
            })}
          </div>

          {selected && sectors ? (
            <>
              <SubHeading right={<Segmented options={radii.map((r) => ({ value: r.radius_miles, label: `${r.radius_miles} mi` }))} value={selected.radius_miles} onChange={setRadius} ariaLabel="Radius" />}>
                Top sectors within {selected.radius_miles} mi
              </SubHeading>
              <ChartFrame
                subtitle="Jobs located in the radius vs jobs held by residents of the radius"
                legend={[{ id: 'jobs', label: 'Jobs located here', color: 'var(--le-s1)' }, { id: 'residents', label: 'Residents’ jobs', color: 'var(--le-s2)' }]}
                table={{
                  columns: [
                    { key: 'label', label: 'Sector' },
                    { key: 'jobs', label: 'Jobs located', align: 'right', format: (v) => fmtNum(v) },
                    { key: 'residents', label: 'Resident workers', align: 'right', format: (v) => fmtNum(v) },
                  ],
                  rows: sectors.all,
                }}
              >
                <HBarList rows={sectors.top.map((r) => ({ label: r.label, values: [r.jobs, r.residents] }))} series={[{ id: 'jobs', label: 'Jobs located here', color: 'var(--le-s1)' }, { id: 'residents', label: 'Residents’ jobs', color: 'var(--le-s2)' }]} valueFormat={(v) => fmtCompact(v)} />
              </ChartFrame>

              <SubHeading>Wage depth &amp; workforce age · {selected.radius_miles} mi</SubHeading>
              <div className="le-split-grid">
                <SplitBar label="Jobs here by monthly earnings" buckets={selected.jobs_by_monthly_earnings} keys={EARNINGS_KEYS} colors={EARNINGS_COLORS} labels={EARNINGS_LABELS} />
                <SplitBar label="Residents' jobs by monthly earnings" buckets={selected.resident_worker_jobs_by_monthly_earnings} keys={EARNINGS_KEYS} colors={EARNINGS_COLORS} labels={EARNINGS_LABELS} />
                <SplitBar label="Jobs here by worker age" buckets={selected.jobs_by_worker_age} keys={AGE_KEYS} colors={EARNINGS_COLORS} labels={WORKER_AGE_LABELS} />
                <SplitBar label="Residents' jobs by worker age" buckets={selected.resident_worker_jobs_by_worker_age} keys={AGE_KEYS} colors={EARNINGS_COLORS} labels={WORKER_AGE_LABELS} />
              </div>
              {isNum(selected.resident_worker_jobs_per_square_mile) ? (
                <div className="le-footnotes muted">Density: {fmtNum(selected.jobs_located_per_square_mile)} jobs / sq mi located here vs {fmtNum(selected.resident_worker_jobs_per_square_mile)} resident workers / sq mi{isNum(selected.jobs_to_resident_worker_jobs_ratio) ? ` · ratio ${fmtPct(selected.jobs_to_resident_worker_jobs_ratio * 100, 0)} of residents' jobs` : ''}</div>
              ) : null}
            </>
          ) : null}

          {notes.length ? <div className="le-footnotes">{notes.map((n) => <div key={n}>{n}</div>)}</div> : null}
        </>
      ) : null}
    </SectionCard>
  );
}
